<script setup>
import EmptyState from '@/components/EmptyState';
import { ref, inject, onMounted } from 'vue';
import { trans } from 'laravel-vue-i18n';
import TireSetSlideOver from '@/components/TireSets/TireSetSlideOver';
import { useTireSetStore } from '@/stores/tireSet';
import { storeToRefs } from 'pinia';

const tireSetStore = useTireSetStore();
const { search, show } = tireSetStore;
const { all, current } = storeToRefs(tireSetStore);

const params = ref({
    term: '',
    season: null,
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const slideOverOpen = ref(false);

const searchSets = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchSets();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchSets();
};

const openCheckIn = () => {
    modals.show('CheckInModal', {}).then(() => {
        searchSets();
    }).catch(() => {
        // Modal cancelled
    });
};

onMounted(() => {
    const query = new URLSearchParams(window.location.search);
    const term = query.get('term');
    if (term) {
        params.value.term = term;
        searchSets();
    }
    if (query.get('check_in')) {
        openCheckIn();
    }
});

const openSet = async (item) => {
    current.value = null;
    slideOverOpen.value = true;
    await show(item.hash);
};

const seasonColor = (season) => {
    if (season === 'winter') {
        return 'winter';
    }
    if (season === 'summer') {
        return 'summer';
    }
    return 'neutral';
};

const tireSubLabel = (item) => {
    const parts = [item.size];
    if (item.has_rims) {
        parts.push(`${trans('label.on_rims')} (${item.quantity})`);
    }
    return parts.filter(Boolean).join(' · ');
};
</script>

<template>
<MainContentLayout>
    <template #title>{{ $t('label.tire_sets') }}</template>
    <template #actions>
        <StyledButton @click="openCheckIn"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.check_in') }}
        </StyledButton>
    </template>
    <div class="rounded-xl border border-line bg-surface p-4 mb-4">
        <div class="grid lg:grid-cols-3 gap-x-3">
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchSets"
                name="search"
                :label="$t('label.search')"
                :debounce-events="['input']"
                @click:clear="clearSearch" />
            <StyledDropdown
                v-model="params.season"
                clearable
                :placeholder="$t('label.season')"
                :options="[
                    { label: $t('label.winter'), value: 'winter' },
                    { label: $t('label.summer'), value: 'summer' },
                    { label: $t('label.all_season'), value: 'all_season' },
                ]"
                name="season"
                @input="searchSets" />
            <StyledDropdown
                v-model="params.status"
                clearable
                :placeholder="$t('label.status')"
                :options="[
                    { label: $t('label.in_storage'), value: 'stored' },
                    { label: $t('label.on_vehicle'), value: 'out' },
                ]"
                name="status"
                @input="searchSets" />
        </div>
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.set_id'), key: 'barcode', value: item => item.barcode, sortable: false },
            { title: $t('label.customer'), key: 'customer_name', value: item => item.customer_name ?? '', sortable: false },
            { title: $t('label.vehicle'), key: 'vehicle', value: item => item.vehicle_label ?? '', sortable: false },
            { title: $t('label.season'), key: 'season', value: item => item.season ?? '', sortable: false },
            { title: $t('label.tires'), key: 'tires', value: item => item.tire_label || item.size || '', sortable: false },
            { title: $t('label.location'), key: 'location', value: item => item.location_label ?? '', sortable: false },
        ]"
        :actions="[
            { title: $t('label.set_details'), icon: 'mdi-eye-outline', action: (item) => openSet(item) },
        ]"
        @table-update="tableUpdate">
        <template #[`item.barcode`]="{ item }">
            <button
                class="font-mono text-xs font-medium text-accent hover:underline"
                @click="openSet(item)">{{ item.barcode }}</button>
        </template>
        <template #[`item.customer_name`]="{ item }">
            <span class="font-semibold">{{ item.customer_name }}</span>
        </template>
        <template #[`item.vehicle`]="{ item }">
            <span class="block">{{ item.vehicle_label }}</span>
            <span
                v-if="item.vin"
                class="font-mono text-xs text-ink-light">{{ item.vin }}</span>
        </template>
        <template #[`item.season`]="{ item }">
            <StatusChip
                v-if="item.season"
                :color="seasonColor(item.season)">{{ $t('label.' + item.season) }}</StatusChip>
        </template>
        <template #[`item.tires`]="{ item }">
            <span class="block">{{ item.tire_label || item.size }}</span>
            <span class="text-xs text-ink-light">{{ tireSubLabel(item) }}</span>
        </template>
        <template #[`item.location`]="{ item }">
            <span
                v-if="item.location_type === 'bin'"
                class="font-mono text-xs rounded px-2 py-1 bg-canvas border border-line">{{ item.location_label }}</span>
            <StatusChip
                v-else
                :color="item.location_type === 'mixed' ? 'warning' : 'neutral'">
                {{ item.location_type === 'mixed' ? $t('label.mixed_locations') : $t('label.on_vehicle') }}
            </StatusChip>
        </template>
        <template #no-data>
            <EmptyState
                icon="mdi-circle-double"
                :title="$t('label.no_tire_sets_found')"
                :hint="$t('label.no_tire_sets_hint')">
                <StyledButton @click="openCheckIn"><FontAwesomeIcon icon="plus" class="mr-1" />
                    {{ $t('label.check_in') }}
                </StyledButton>
            </EmptyState>
        </template>
    </DataTable>

    <TireSetSlideOver
        v-if="slideOverOpen"
        @close="slideOverOpen = false" />
</MainContentLayout>
</template>
